import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { MonthPayments } from '../../lib/daily-payments';
import { formatRupiah } from '../../lib/format';
import { useLanguage } from '../../i18n';
import { chartColors } from '../../theme';

/**
 * Payments per day across one month, with the due date marked. Shows when
 * residents actually pay: the rush before the due date and the tail after it.
 */
export function DailyPaymentsCard({ data, monthLabel }: { data: MonthPayments; monthLabel: string }) {
  const { t } = useLanguage();
  const theme = useTheme();
  const colors = chartColors(theme.palette.mode);
  const HEIGHT = 120;
  const max = Math.max(1, ...data.days.map((d) => d.count));
  const n = data.days.length;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600}>{t('bill.dailyTitle', { month: monthLabel })}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {data.totalCount === 0
            ? t('bill.dailyEmpty')
            : t('bill.dailySummary', { count: data.totalCount, amount: formatRupiah(data.totalCents) })}
        </Typography>

        <Box sx={{ position: 'relative', height: HEIGHT }}>
          <Stack direction="row" spacing={0.375} alignItems="flex-end" sx={{ height: '100%' }}>
            {data.days.map((d) => {
              const afterDue = data.dueDay !== null && d.day > data.dueDay;
              return (
                <Box
                  key={d.day}
                  title={d.future
                    ? `${d.day} ${monthLabel}`
                    : `${d.day} ${monthLabel} · ${t('bill.dailyTooltip', { count: d.count, amount: formatRupiah(d.amountCents) })}`}
                  sx={{
                    flex: 1,
                    height: d.count > 0 ? `${(d.count / max) * 100}%` : 2,
                    minHeight: 2,
                    borderRadius: '3px 3px 0 0',
                    bgcolor: d.future
                      ? 'transparent'
                      : d.count === 0
                        ? theme.palette.action.hover
                        : afterDue
                          ? colors.late
                          : colors.primary,
                    border: d.future ? `1px dashed ${theme.palette.divider}` : 'none',
                  }}
                />
              );
            })}
          </Stack>
          {/* Due date: a dashed vertical line at the end of the due day. */}
          {data.dueDay !== null ? (
            <>
              <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(data.dueDay / n) * 100}%`, borderLeft: '1.5px dashed', borderColor: 'text.secondary', opacity: 0.6, pointerEvents: 'none' }} />
              <Typography
                variant="caption"
                sx={{ position: 'absolute', top: -4, left: `calc(${(data.dueDay / n) * 100}% + 4px)`, fontSize: '0.6875rem', color: 'text.secondary', bgcolor: 'background.paper', px: 0.5 }}
              >
                {t('bill.dailyDue', { day: data.dueDay })}
              </Typography>
            </>
          ) : null}
        </Box>

        {/* Day numbers: every day on wide screens would crowd, so label 1, 5, 10, … and the last day. */}
        <Stack direction="row" spacing={0.375} sx={{ mt: 0.5 }}>
          {data.days.map((d) => (
            <Box key={d.day} sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>
                {d.day === 1 || d.day % 5 === 0 || d.day === n ? d.day : ''}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Legend color={colors.primary} label={t('bill.dailyBeforeDue')} />
          <Legend color={colors.late} label={t('bill.dailyAfterDue')} />
        </Stack>
      </CardContent>
    </Card>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
  );
}
